import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { Plus, Trash2, Edit2, Save, X, MoreVertical } from 'react-feather';

const ProductTemplateManager = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [newTabName, setNewTabName] = useState('');
  const [showAddTab, setShowAddTab] = useState(false);

  // Field types available
  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'number', label: 'Number' },
    { value: 'select', label: 'Dropdown' },
    { value: 'multiselect', label: 'Multi-Select' }
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedSubcategory) {
      loadTemplate();
    }
  }, [selectedSubcategory]);

  const loadCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = [];
      querySnapshot.forEach((doc) => {
        categoriesData.push({ id: doc.id, ...doc.data() });
      });
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async () => {
    if (!selectedSubcategory) return;
    
    try {
      setLoading(true);
      const docRef = doc(db, 'settings', `productTemplate_${selectedSubcategory}`);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setTabs(docSnap.data().tabs || []);
      } else {
        setTabs([]);
      }
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!selectedSubcategory) {
      alert('Please select a subcategory first');
      return;
    }
    
    setSaving(true);
    try {
      const docRef = doc(db, 'settings', `productTemplate_${selectedSubcategory}`);
      await setDoc(docRef, { tabs }, { merge: true });
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
    }
    setSaving(false);
  };

  const addTab = () => {
    if (!newTabName.trim()) return;
    
    const newTab = {
      id: newTabName.toLowerCase().replace(/\s+/g, '-'),
      name: newTabName,
      order: tabs.length + 1,
      fields: []
    };
    
    setTabs([...tabs, newTab]);
    setNewTabName('');
    setShowAddTab(false);
  };

  const deleteTab = (tabId) => {
    if (window.confirm('Delete this tab and all its fields?')) {
      setTabs(tabs.filter(tab => tab.id !== tabId));
    }
  };

  const addField = (tabId) => {
    const newField = {
      id: `field-${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      options: []
    };

    setTabs(tabs.map(tab => 
      tab.id === tabId 
        ? { ...tab, fields: [...tab.fields, newField] }
        : tab
    ));
  };

  const updateField = (tabId, fieldId, updates) => {
    setTabs(tabs.map(tab => 
      tab.id === tabId 
        ? {
            ...tab,
            fields: tab.fields.map(field => 
              field.id === fieldId ? { ...field, ...updates } : field
            )
          }
        : tab
    ));
  };

  const deleteField = (tabId, fieldId) => {
    if (window.confirm('Delete this field?')) {
      setTabs(tabs.map(tab => 
        tab.id === tabId 
          ? { ...tab, fields: tab.fields.filter(field => field.id !== fieldId) }
          : tab
      ));
    }
  };

  const moveTab = (fromIndex, toIndex) => {
    const newTabs = [...tabs];
    const [movedTab] = newTabs.splice(fromIndex, 1);
    newTabs.splice(toIndex, 0, movedTab);
    
    // Update order
    newTabs.forEach((tab, index) => {
      tab.order = index + 1;
    });
    
    setTabs(newTabs);
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading template...</div>;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '32px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
            Product Information Template
          </h2>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            Configure the tabs and fields that appear on the left side of product details
          </p>
        </div>
        <button
          onClick={saveTemplate}
          disabled={saving || !selectedSubcategory}
          style={{
            background: (!selectedSubcategory || saving) ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: (saving || !selectedSubcategory) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Template'}
        </button>
      </div>

      {/* Category and Subcategory Selection */}
      <div style={{
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
          Select Subcategory to Configure
        </h3>
        
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedSubcategory('');
                setTabs([]);
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.displayName || category.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              Subcategory
            </label>
            <select
              value={selectedSubcategory}
              onChange={(e) => setSelectedSubcategory(e.target.value)}
              disabled={!selectedCategory}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
                opacity: !selectedCategory ? 0.5 : 1
              }}
            >
              <option value="">Select Subcategory</option>
              {selectedCategory && categories.find(c => c.id === selectedCategory)?.subcategories?.map((subcategory, index) => (
                <option key={index} value={subcategory.name || subcategory}>
                  {subcategory.name || subcategory}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedSubcategory && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            background: '#e8f5e8', 
            borderRadius: '6px',
            fontSize: '14px',
            color: '#2d5a3d'
          }}>
            <strong>Configuring template for:</strong> {selectedCategory} → {selectedSubcategory}
          </div>
        )}
      </div>

      {!selectedSubcategory && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          background: '#f8f9fa',
          borderRadius: '12px',
          color: '#666',
          fontSize: '16px'
        }}>
          Please select a category and subcategory to configure its template
        </div>
      )}

      {/* Tabs List - only show when subcategory is selected */}
      {selectedSubcategory && (
        <div style={{ marginBottom: '24px' }}>
          {tabs.map((tab, tabIndex) => (
          <div key={tab.id} style={{
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            marginBottom: '16px',
            overflow: 'hidden',
            backgroundColor: 'white'
          }}>
            {/* Tab Header */}
            <div style={{
              background: '#f8f9fa',
              padding: '16px 20px',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MoreVertical size={16} color="#999" />
                {editingTab === tab.id ? (
                  <input
                    type="text"
                    value={tab.name}
                    onChange={(e) => setTabs(tabs.map(t => 
                      t.id === tab.id ? { ...t, name: e.target.value } : t
                    ))}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                    onBlur={() => setEditingTab(null)}
                    onKeyPress={(e) => e.key === 'Enter' && setEditingTab(null)}
                    autoFocus
                  />
                ) : (
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                    {tab.name}
                  </h3>
                )}
                <span style={{ fontSize: '12px', color: '#666', background: '#e9ecef', padding: '2px 8px', borderRadius: '12px' }}>
                  {tab.fields.length} fields
                </span>
              </div>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setEditingTab(tab.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '4px'
                  }}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => addField(tab.id)}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Plus size={12} />
                  Add Field
                </button>
                <button
                  onClick={() => deleteTab(tab.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#dc3545',
                    padding: '4px'
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {/* Fields List */}
            <div style={{ padding: '20px' }}>
              {tab.fields.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  color: '#999',
                  fontSize: '14px',
                  padding: '20px'
                }}>
                  No fields defined. Click "Add Field" to create custom fields.
                </div>
              ) : (
                tab.fields.map((field, fieldIndex) => (
                  <div key={field.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 100px 80px 40px',
                    gap: '12px',
                    alignItems: 'center',
                    padding: '12px',
                    background: field.id === editingField ? '#f8f9fa' : 'transparent',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}>
                    <input
                      type="text"
                      placeholder="Field Label"
                      value={field.label}
                      onChange={(e) => updateField(tab.id, field.id, { label: e.target.value })}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                    
                    <select
                      value={field.type}
                      onChange={(e) => updateField(tab.id, field.id, { type: e.target.value })}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    >
                      {fieldTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>

                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(tab.id, field.id, { required: e.target.checked })}
                      />
                      Required
                    </label>

                    <span style={{ 
                      fontSize: '12px', 
                      color: '#666',
                      textAlign: 'center'
                    }}>
                      {field.id}
                    </span>

                    <button
                      onClick={() => deleteField(tab.id, field.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#dc3545',
                        padding: '4px'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Add New Tab - only show when subcategory is selected */}
      {selectedSubcategory && (
        <div style={{
          border: '2px dashed #dee2e6',
          borderRadius: '12px',
          padding: '24px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          marginTop: '16px'
        }}>
          {showAddTab ? (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Tab name (e.g., 'Brewing Guide')"
                value={newTabName}
                onChange={(e) => setNewTabName(e.target.value)}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  width: '200px'
                }}
              />
              <button
                onClick={addTab}
                style={{
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: 'pointer'
                }}
              >
                Add
              </button>
              <button
                onClick={() => setShowAddTab(false)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddTab(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#007bff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
            >
              <Plus size={16} />
              Add New Tab
            </button>
          )}
        </div>
      )}

      {/* Preview Section */}
      {selectedSubcategory && (
      <div style={{ marginTop: '40px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
          Preview - How it will look:
        </h3>
        <div style={{
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                style={{
                  background: index === 0 ? '#007bff' : 'transparent',
                  color: index === 0 ? 'white' : '#666',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {tab.name}
              </button>
            ))}
          </div>
          
          {tabs[0] && (
            <div>
              <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                {tabs[0].name}:
              </h4>
              {tabs[0].fields.map(field => (
                <div key={field.id} style={{ marginBottom: '12px' }}>
                  <span style={{ fontWeight: '500', fontSize: '14px', color: '#333' }}>
                    {field.label}:
                  </span>
                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                    [{field.type}] {field.required && '(Required)'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default ProductTemplateManager;